import { geographicMarketSchema, type GeographicMarket } from "@runcheetah/shared";

export type AudienceMarketDraft = {
  scope: GeographicMarket["scope"];
  countryCode: string;
  regionCode: string;
  label: string;
  rationale: string;
};

export type AudienceMarketCountryOption = {
  countryCode: string;
  name: string;
};

export type AudienceMarketRegionOption = {
  regionCode: string;
  name: string;
};

export const shouldHydrateAudienceMarketDrafts = (input: {
  brandId: string | null | undefined;
  hydratedBrandId: string | null;
  isDirty: boolean;
}): boolean =>
  Boolean(input.brandId && (input.hydratedBrandId !== input.brandId || !input.isDirty));

export const createAudienceMarketDraft = (
  scope: AudienceMarketDraft["scope"] = "country",
): AudienceMarketDraft => ({
  scope,
  countryCode: "",
  regionCode: "",
  label: scope === "global" ? "Global" : "",
  rationale: "",
});

export const applyAudienceMarketCountryOption = (
  draft: AudienceMarketDraft,
  option: AudienceMarketCountryOption,
): AudienceMarketDraft => ({
  ...draft,
  scope: "country",
  countryCode: option.countryCode,
  regionCode: "",
  label: option.name,
});

export const applyAudienceMarketRegionOption = (
  draft: AudienceMarketDraft,
  option: AudienceMarketRegionOption,
): AudienceMarketDraft => ({
  ...draft,
  scope: "region",
  countryCode: "",
  regionCode: option.regionCode,
  label: option.name,
});

export const toAudienceMarketDrafts = (
  markets: readonly GeographicMarket[] | null | undefined,
): AudienceMarketDraft[] =>
  (markets ?? []).map((market) => ({
    scope: market.scope,
    countryCode: market.countryCode ?? "",
    regionCode: market.regionCode ?? "",
    label: market.label,
    rationale: market.rationale,
  }));

const hasDraftContent = (draft: AudienceMarketDraft): boolean =>
  Boolean(
    draft.countryCode.trim() ||
    draft.regionCode.trim() ||
    draft.label.trim() ||
    draft.rationale.trim() ||
    draft.scope === "global",
  );

export const buildAudienceMarketsFromDrafts = (
  drafts: readonly AudienceMarketDraft[],
): GeographicMarket[] =>
  drafts.filter(hasDraftContent).map((draft) => {
    const countryCode = draft.countryCode.trim().toUpperCase();
    const regionCode = draft.regionCode.trim().toUpperCase();
    const label = draft.label.trim() || countryCode || regionCode || "Global";
    const rationale = draft.rationale.trim() || "Manually set by user.";

    return geographicMarketSchema.parse({
      scope: draft.scope,
      countryCode: draft.scope === "country" ? countryCode : null,
      regionCode: draft.scope === "region" ? regionCode : null,
      label,
      confidence: "explicit",
      rationale,
    });
  });

export const formatAudienceMarketLabel = (market: GeographicMarket): string => market.label;
